import {
  Component,
  Inject,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VoucherEligibilityItem, CartAfterRemovingIneligibleVouchers } from '@/app/shared/interface/checkout-v2.interface';
import { AppliedVoucher } from '@/app/shared/interface/cart.interface';
import { Voucher } from '@/app/shared/interface/voucher.interface';

export interface IneligibleVoucherDisplay {
  eligibility: VoucherEligibilityItem;
  appliedVoucher?: AppliedVoucher;
  voucher: Voucher;
}

@Component({
  selector: 'app-voucher-ineligible-modal',
  templateUrl: './voucher-ineligible-modal.component.html',
  styleUrls: ['./voucher-ineligible-modal.component.scss'],
})
export class VoucherIneligibleModalComponent implements OnDestroy {
  @ViewChild('voucherIneligibleModal', { static: false })
  modalTemplate: TemplateRef<any>;

  displayItems: IneligibleVoucherDisplay[] = [];
  cartAfter: CartAfterRemovingIneligibleVouchers | null = null;

  modalOpen = false;
  private resolveModal: ((confirmed: boolean) => void) | null = null;

  constructor(
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Mở modal và trả về Promise<boolean>:
   * - true  = người dùng chọn "Tiếp tục và không sử dụng voucher"
   * - false = người dùng chọn "Kiểm tra lại đơn hàng" hoặc đóng modal
   */
  openModal(
    ineligibleVouchers: VoucherEligibilityItem[],
    cartAfter: CartAfterRemovingIneligibleVouchers,
    appliedVouchers: AppliedVoucher[] = [],
  ): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve(false);

    this.cartAfter = cartAfter;
    this.displayItems = ineligibleVouchers.map(ev => {
      const appliedVoucher = appliedVouchers.find(av => av.code === ev.voucherCode);
      return {
        eligibility: ev,
        appliedVoucher,
        voucher: this.toVoucher(ev, appliedVoucher),
      };
    });
    this.modalOpen = true;
    this.cdr.detectChanges();

    return new Promise<boolean>((resolve) => {
      this.resolveModal = resolve;
      this.modalService
        .open(this.modalTemplate, {
          ariaLabelledBy: 'voucher-ineligible-modal-title',
          centered: true,
          size: 'md',
          windowClass: 'voucher-ineligible-modal-window',
          backdrop: 'static',
          keyboard: false,
          scrollable: true,
        })
        .result.then(
          () => { this.modalOpen = false; },
          () => {
            this.modalOpen = false;
            if (this.resolveModal) {
              this.resolveModal(false);
              this.resolveModal = null;
            }
          },
        );
    });
  }

  /** Người dùng chọn "Tiếp tục và không sử dụng voucher" */
  onContinue(): void {
    if (this.resolveModal) {
      this.resolveModal(true);
      this.resolveModal = null;
    }
    this.modalService.dismissAll('continue');
  }

  /** Người dùng chọn "Kiểm tra lại đơn hàng" */
  onReview(): void {
    this.modalService.dismissAll('review');
  }

  /** Chuyển AppliedVoucher sang Voucher để dùng với app-voucher-item */
  private toVoucher(ev: VoucherEligibilityItem, av?: AppliedVoucher): Voucher {
    return {
      ...(av as any),
      code: av?.code ?? ev.voucherCode,
      descriptionVoucher: (av as any)?.descriptionVoucher ?? av?.description,
      tags: (av as any)?.tags ?? [],
      remainingUses: av?.remainingUses ?? 0,
      isApplied: false,
      isApplicable: false,
    } as Voucher;
  }

  ngOnDestroy(): void {
    if (this.modalOpen) this.modalService.dismissAll();
  }
}
